import { api } from '../../lib/api.ts';

/** Field types for actual data fields (SECTION is handled via createSection). */
export type FieldType = 'TEXT' | 'NUMBER' | 'SELECT' | 'CHOICE' | 'MULTISELECT' | 'DATE';
export type FormEntity = 'RESIDENT' | 'REGISTERED_BUSINESS' | 'HOME_BUSINESS';

/** A data field that belongs to a section. */
export interface Field {
  id: string;
  label: string;
  type: FieldType;
  options: string[] | null;
  required: boolean;
  orderIndex: number;
  sectionId: string;
  /** True for seeded defaults (locked: cannot be deleted; label/type are fixed). */
  system: boolean;
  /** Stable identifier for system fields (e.g. "fullName"); null otherwise. */
  key: string | null;
}

/** A section groups fields; sections cannot themselves be nested. */
export interface Section {
  id: string;
  label: string;
  type: 'SECTION';
  orderIndex: number;
  fields: Field[];
  /** True for the seeded "Basic Information" section (cannot be deleted). */
  system: boolean;
  key: string | null;
}

export interface FieldInput {
  type: FieldType;
  label: string;
  options?: string[];
  required?: boolean;
}

export type FieldPatch = Partial<{
  label: string;
  options: string[] | null;
  required: boolean;
}>;

export const formsApi = {
  list: (entity: FormEntity) =>
    api
      .get<{ sections: Section[] }>('/form-fields', { params: { entity } })
      .then((r) => r.data.sections),

  createSection: (entity: FormEntity, label: string) =>
    api
      .post<{ field: Section }>('/form-fields', { entity, type: 'SECTION', label })
      .then((r) => r.data.field),

  createField: (entity: FormEntity, sectionId: string, input: FieldInput) =>
    api
      .post<{ field: Field }>('/form-fields', { entity, sectionId, ...input })
      .then((r) => r.data.field),

  updateField: (id: string, patch: FieldPatch) =>
    api.patch<{ field: Field }>(`/form-fields/${id}`, patch).then((r) => r.data.field),

  remove: (id: string) => api.delete(`/form-fields/${id}`).then(() => undefined),

  reorderSections: (entity: FormEntity, orderedIds: string[]) =>
    api.post('/form-fields/reorder', { entity, orderedIds }).then(() => undefined),

  reorderFields: (entity: FormEntity, sectionId: string, orderedIds: string[]) =>
    api.post('/form-fields/reorder', { entity, sectionId, orderedIds }).then(() => undefined),
};
